import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Info, Type } from "lucide-react";
import { useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Import Adinkra symbols
import aban from "@/assets/symbols/Aban.svg";
import abeDua from "@/assets/symbols/Abe Dua.svg";
import abusuaPa from "@/assets/symbols/Abusua Pa.svg";
import adinkrahene from "@/assets/symbols/Adinkrahene.svg";
import adwo from "@/assets/symbols/Adwo.svg";
import agyindawuru from "@/assets/symbols/Agyindawuru.svg";
import akoben from "@/assets/symbols/Akoben.svg";
import akofena from "@/assets/symbols/Akofena.svg";
import akokoNan from "@/assets/symbols/Akoko Nan.svg";
import akomaNtoso from "@/assets/symbols/Akoma Ntoso.svg";
import akoma from "@/assets/symbols/Akoma.svg";
import ananseNtontan from "@/assets/symbols/Ananse Ntontan.svg";
import aniBereAEnsoGya from "@/assets/symbols/Ani Bere a Enso Gya.svg";
import asaawa from "@/assets/symbols/Asaawa.svg";
import asaseYeDuru from "@/assets/symbols/Asase Ye Duru.svg";
import asetenaPa from "@/assets/symbols/Asetena Pa.svg";
import awuradeBaatanfo from "@/assets/symbols/Awurade Baatanfo.svg";
import aya from "@/assets/symbols/Aya.svg";
import beseSaka from "@/assets/symbols/Bese Saka.svg";
import biNkaBi from "@/assets/symbols/Bi Nka Bi.svg";
import boaMeNaMeMmoaWo from "@/assets/symbols/Boa Me Na Me Mmoa Wo.svg";
import boafoYeNa from "@/assets/symbols/Boafo Ye Na.svg";
import dameDame from "@/assets/symbols/Dame Dame.svg";
import denkyem from "@/assets/symbols/Denkyem.svg";
import donoNtoaso from "@/assets/symbols/Dono Ntoaso.svg";
import dono from "@/assets/symbols/Dono.svg";
import duafe from "@/assets/symbols/Duafe.svg";
import dwennimmen from "@/assets/symbols/Dwennimmen.svg";
import eban from "@/assets/symbols/Eban.svg";
import epa from "@/assets/symbols/Epa.svg";
import eseNeTekrema from "@/assets/symbols/Ese Ne Tekrema.svg";
import esonoAnantam from "@/assets/symbols/Esono Anantam.svg";
import fafanto from "@/assets/symbols/Fafanto.svg";
import fawohodie from "@/assets/symbols/Fawohodie.svg";
import fihankra from "@/assets/symbols/Fihankra.svg";
import fofo from "@/assets/symbols/Fofo.svg";
import funtunfunefuDenkyemfunefu from "@/assets/symbols/Funtunfunefu Denkyemfunefu.svg";
import gyeNyame from "@/assets/symbols/Gye Nyame.svg";
import gyeWAni from "@/assets/symbols/Gye W'ani.svg";
import hweMuDua from "@/assets/symbols/Hwe Mu Dua.svg";
import hyeWonHye from "@/assets/symbols/Hye won hye.svg";
import kintinkantan from "@/assets/symbols/Kintinkantan.svg";
import kokuromotie from "@/assets/symbols/Kokuromotie.svg";
import kramoBoneAmmaYeanhuKramoPa from "@/assets/symbols/Kramo Bone Amma Yeanhu Kramo Pa.svg";
import kwatakyeAtiko from "@/assets/symbols/Kwatakye Atiko.svg";
import kyemfere from "@/assets/symbols/Kyemfere.svg";
import mako from "@/assets/symbols/Mako.svg";
import mateMasie from "@/assets/symbols/Mate Masie.svg";
import meWareWo from "@/assets/symbols/Me Ware Wo.svg";
import mekyiaWo from "@/assets/symbols/Mekyia Wo.svg";
import mensoWoKenten from "@/assets/symbols/Menso Wo Kenten.svg";
import academicCityLogo from "@/assets/symbols/acity.png";
import ashesiLogo from "@/assets/symbols/ashesi.png";
import knustPngLogo from "@/assets/symbols/Knust.png";
import uccLogo from "@/assets/symbols/ucc.png";
import ugLogo from "@/assets/symbols/ug.png";
import winnebaLogo from "@/assets/symbols/winneba.png";
import umLogo from "@/assets/symbols/Um.png";
import chatgptLogo from "@/assets/symbols/chatgpt.png";
import medicalLogo from "@/assets/symbols/medical-logo-svgrepo-com.png";

interface Symbol {
  id: string;
  name: string;
  meaning: string;
  image: string;
  category: "adinkra" | "other";
}

interface SymbolSelectorProps {
  onAddSymbol: (symbol: Symbol) => void;
  onAddText?: (text: string) => void;
  onClose?: () => void;
}

const symbols: Symbol[] = [
  {
    id: "aban",
    name: "Aban",
    meaning: "Fortress - Strength, security, and safety",
    image: aban,
    category: "adinkra",
  },
  {
    id: "abe-dua",
    name: "Abe Dua",
    meaning: "Palm tree - Wealth, self-sufficiency, and resourcefulness",
    image: abeDua,
    category: "adinkra",
  },
  {
    id: "abusua-pa",
    name: "Abusua Pa",
    meaning: "Good family - Unity, harmony, and family values",
    image: abusuaPa,
    category: "adinkra",
  },
  {
    id: "adinkrahene",
    name: "Adinkrahene",
    meaning: "Chief of Adinkra - Greatness, charisma, and leadership",
    image: adinkrahene,
    category: "adinkra",
  },
  {
    id: "adwo",
    name: "Adwo",
    meaning: "Peace - Calmness, tranquility, and peace of mind",
    image: adwo,
    category: "adinkra",
  },
  {
    id: "agyindawuru",
    name: "Agyindawuru",
    meaning: "War horn - Readiness, alertness, and vigilance",
    image: agyindawuru,
    category: "adinkra",
  },
  {
    id: "akoben",
    name: "Akoben",
    meaning: "War horn - Readiness, alertness, and vigilance",
    image: akoben,
    category: "adinkra",
  },
  {
    id: "akofena",
    name: "Akofena",
    meaning: "Sword of war - Courage, valor, and heroism",
    image: akofena,
    category: "adinkra",
  },
  {
    id: "akoko-nan",
    name: "Akoko Nan",
    meaning: "Hen's feet - Nurturing, motherhood, and care",
    image: akokoNan,
    category: "adinkra",
  },
  {
    id: "akoma-ntoso",
    name: "Akoma Ntoso",
    meaning: "Linked hearts - Understanding and agreement",
    image: akomaNtoso,
    category: "adinkra",
  },
  {
    id: "akoma",
    name: "Akoma",
    meaning: "Heart - Patience and tolerance",
    image: akoma,
    category: "adinkra",
  },
  {
    id: "ananse-ntontan",
    name: "Ananse Ntontan",
    meaning: "Spider's web - Wisdom, creativity, and complexity",
    image: ananseNtontan,
    category: "adinkra",
  },
  {
    id: "ani-bere-a-enso-gya",
    name: "Ani Bere a Enso Gya",
    meaning:
      "No one goes to the house of the rainbow twice - Life's uniqueness",
    image: aniBereAEnsoGya,
    category: "adinkra",
  },
  {
    id: "asaawa",
    name: "Asaawa",
    meaning: "Gourd - Unity, cooperation, and sharing",
    image: asaawa,
    category: "adinkra",
  },
  {
    id: "asase-ye-duru",
    name: "Asase Ye Duru",
    meaning: "The earth has weight - Divine nature of Mother Earth",
    image: asaseYeDuru,
    category: "adinkra",
  },
  {
    id: "asetena-pa",
    name: "Asetena Pa",
    meaning: "Good living - Peace, harmony, and prosperity",
    image: asetenaPa,
    category: "adinkra",
  },
  {
    id: "awurade-baatanfo",
    name: "Awurade Baatanfo",
    meaning: "God is my friend - Divine friendship and protection",
    image: awuradeBaatanfo,
    category: "adinkra",
  },
  {
    id: "aya",
    name: "Aya",
    meaning: "Fern - Endurance, resourcefulness, and perseverance",
    image: aya,
    category: "adinkra",
  },
  {
    id: "bese-saka",
    name: "Bese Saka",
    meaning: "Sack of cola nuts - Affluence, power, and abundance",
    image: beseSaka,
    category: "adinkra",
  },
  {
    id: "bi-nka-bi",
    name: "Bi Nka Bi",
    meaning: "No one should bite the other - Peace, harmony, and justice",
    image: biNkaBi,
    category: "adinkra",
  },
  {
    id: "boa-me-na-me-mmoa-wo",
    name: "Boa Me Na Me Mmoa Wo",
    meaning: "Help me and let me help you - Cooperation and interdependence",
    image: boaMeNaMeMmoaWo,
    category: "adinkra",
  },
  {
    id: "boafo-ye-na",
    name: "Boafo Ye Na",
    meaning: "A good friend is a treasure - Friendship and loyalty",
    image: boafoYeNa,
    category: "adinkra",
  },
  {
    id: "dame-dame",
    name: "Dame Dame",
    meaning: "Checkers - Intelligence and strategy",
    image: dameDame,
    category: "adinkra",
  },
  {
    id: "denkyem",
    name: "Denkyem",
    meaning: "Crocodile - Adaptability and prudence",
    image: denkyem,
    category: "adinkra",
  },
  {
    id: "dono-ntoaso",
    name: "Dono Ntoaso",
    meaning: "Double dono - Unity and harmony",
    image: donoNtoaso,
    category: "adinkra",
  },
  {
    id: "dono",
    name: "Dono",
    meaning: "Talking drum - Communication and unity",
    image: dono,
    category: "adinkra",
  },
  {
    id: "duafe",
    name: "Duafe",
    meaning: "Wooden comb - Beauty, cleanliness, and feminine qualities",
    image: duafe,
    category: "adinkra",
  },
  {
    id: "dwennimmen",
    name: "Dwennimmen",
    meaning: "Ram's horns - Humility and strength",
    image: dwennimmen,
    category: "adinkra",
  },
  {
    id: "eban",
    name: "Eban",
    meaning: "Fence - Love, safety, and security",
    image: eban,
    category: "adinkra",
  },
  {
    id: "epa",
    name: "Epa",
    meaning: "Handcuffs - Law and justice",
    image: epa,
    category: "adinkra",
  },
  {
    id: "ese-ne-tekrema",
    name: "Ese Ne Tekrema",
    meaning: "Teeth and tongue - Interdependence and friendship",
    image: eseNeTekrema,
    category: "adinkra",
  },
  {
    id: "esono-anantam",
    name: "Esono Anantam",
    meaning: "Elephant's footprint - Leadership and strength",
    image: esonoAnantam,
    category: "adinkra",
  },
  {
    id: "fafanto",
    name: "Fafanto",
    meaning: "Butterfly - Transformation and beauty",
    image: fafanto,
    category: "adinkra",
  },
  {
    id: "fawohodie",
    name: "Fawohodie",
    meaning: "Independence - Freedom, emancipation, and liberty",
    image: fawohodie,
    category: "adinkra",
  },
  {
    id: "fihankra",
    name: "Fihankra",
    meaning: "House - Security and safety",
    image: fihankra,
    category: "adinkra",
  },
  {
    id: "fofo",
    name: "Fofo",
    meaning: "Flower - Beauty and growth",
    image: fofo,
    category: "adinkra",
  },
  {
    id: "funtunfunefu-denkyemfunefu",
    name: "Funtunfunefu Denkyemfunefu",
    meaning: "Siamese crocodiles - Democracy and unity",
    image: funtunfunefuDenkyemfunefu,
    category: "adinkra",
  },
  {
    id: "gye-nyame",
    name: "Gye Nyame",
    meaning: "Except God - Symbol of the supremacy of God",
    image: gyeNyame,
    category: "adinkra",
  },
  {
    id: "gye-w-ani",
    name: "Gye W'ani",
    meaning: "Except for God's eye - Divine protection and guidance",
    image: gyeWAni,
    category: "adinkra",
  },
  {
    id: "hwe-mu-dua",
    name: "Hwe Mu Dua",
    meaning: "Measuring stick - Excellence and quality",
    image: hweMuDua,
    category: "adinkra",
  },
  {
    id: "hye-won-hye",
    name: "Hye Won Hye",
    meaning: "That which does not burn - Endurance and imperishability",
    image: hyeWonHye,
    category: "adinkra",
  },
  {
    id: "kintinkantan",
    name: "Kintinkantan",
    meaning: "Spread out - Greatness and magnificence",
    image: kintinkantan,
    category: "adinkra",
  },
  {
    id: "kokuromotie",
    name: "Kokuromotie",
    meaning: "Comb - Beauty and feminine qualities",
    image: kokuromotie,
    category: "adinkra",
  },
  {
    id: "kramo-bone-amma-yeanhu-kramo-pa",
    name: "Kramo Bone Amma Yeanhu Kramo Pa",
    meaning:
      "The bad Muslim made it impossible for the good one to be recognized - Discrimination and prejudice",
    image: kramoBoneAmmaYeanhuKramoPa,
    category: "adinkra",
  },
  {
    id: "kwatakye-atiko",
    name: "Kwatakye Atiko",
    meaning: "Atiko's hairstyle - Bravery and valor",
    image: kwatakyeAtiko,
    category: "adinkra",
  },
  {
    id: "kyemfere",
    name: "Kyemfere",
    meaning: "Pot with burnt bottom - Knowledge and experience",
    image: kyemfere,
    category: "adinkra",
  },
  {
    id: "mako",
    name: "Mako",
    meaning: "Peppers - Inequality and unevenness",
    image: mako,
    category: "adinkra",
  },
  {
    id: "mate-masie",
    name: "Mate Masie",
    meaning: "What I hear, I keep - Wisdom, knowledge, and prudence",
    image: mateMasie,
    category: "adinkra",
  },
  {
    id: "me-ware-wo",
    name: "Me Ware Wo",
    meaning: "I shall marry you - Commitment and love",
    image: meWareWo,
    category: "adinkra",
  },
  {
    id: "mekyia-wo",
    name: "Mekyia Wo",
    meaning: "I greet you - Respect and courtesy",
    image: mekyiaWo,
    category: "adinkra",
  },
  {
    id: "menso-wo-kenten",
    name: "Menso Wo Kenten",
    meaning: "I have not eaten your kente - Respect for property",
    image: mensoWoKenten,
    category: "adinkra",
  },
  {
    id: "academic-city-university",
    name: "Academic City University",
    meaning:
      "Academic City University - Innovation and excellence in education",
    image: academicCityLogo,
    category: "other",
  },
  {
    id: "ashesi-university",
    name: "Ashesi University",
    meaning:
      "Ashesi University - Leadership, innovation, and ethical entrepreneurship",
    image: ashesiLogo,
    category: "other",
  },
  {
    id: "knust-png",
    name: "KNUST (PNG)",
    meaning:
      "Kwame Nkrumah University of Science and Technology - Excellence in science and technology",
    image: knustPngLogo,
    category: "other",
  },
  {
    id: "university-of-cape-coast",
    name: "University of Cape Coast",
    meaning: "University of Cape Coast - Excellence in education and research",
    image: uccLogo,
    category: "other",
  },
  {
    id: "university-of-ghana",
    name: "University of Ghana",
    meaning:
      "University of Ghana - Premier university and center of excellence",
    image: ugLogo,
    category: "other",
  },
  {
    id: "university-of-education-winneba",
    name: "University of Education Winneba",
    meaning:
      "University of Education Winneba - Excellence in teacher education",
    image: winnebaLogo,
    category: "other",
  },
  {
    id: "um-logo",
    name: "UM Logo",
    meaning:
      "University of Mines and Technology - Excellence in mining and engineering education",
    image: umLogo,
    category: "other",
  },
  {
    id: "chatgpt-logo",
    name: "ChatGPT Logo",
    meaning: "ChatGPT - Artificial intelligence and modern technology",
    image: chatgptLogo,
    category: "other",
  },
  {
    id: "medical-logo",
    name: "Medical Logo",
    meaning: "Caduceus - Symbol of medicine, healing, and healthcare",
    image: medicalLogo,
    category: "other",
  },
];

export const SymbolSelector = ({
  onAddSymbol,
  onAddText,
  onClose,
}: SymbolSelectorProps) => {
  const [selectedCategory, setSelectedCategory] = useState<
    "all" | "adinkra" | "other" | "text"
  >("all");
  const [textInput, setTextInput] = useState("");
  const [alternativeTextInput, setAlternativeTextInput] = useState("");

  const filteredSymbols =
    selectedCategory === "all"
      ? symbols
      : symbols.filter((symbol) => symbol.category === selectedCategory);

  return (
    <div className="space-y-4">
      {/* Category Filter */}
      <div className="flex gap-1 sm:gap-2 flex-wrap">
        <Button
          variant={selectedCategory === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setSelectedCategory("all")}
          className="text-xs sm:text-sm px-2 sm:px-3"
        >
          All
        </Button>
        <Button
          variant={selectedCategory === "adinkra" ? "default" : "outline"}
          size="sm"
          onClick={() => setSelectedCategory("adinkra")}
          className="text-xs sm:text-sm px-2 sm:px-3"
        >
          Adinkra
        </Button>
        <Button
          variant={selectedCategory === "other" ? "default" : "outline"}
          size="sm"
          onClick={() => setSelectedCategory("other")}
          className="text-xs sm:text-sm px-2 sm:px-3"
        >
          Other
        </Button>
        <Button
          variant={selectedCategory === "text" ? "default" : "outline"}
          size="sm"
          onClick={() => setSelectedCategory("text")}
          className="text-xs sm:text-sm px-2 sm:px-3"
        >
          <Type className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
          Text
        </Button>
      </div>

      {/* Content based on selected category */}
      {selectedCategory === "text" ? (
        /* Text Input Section */
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Add Custom Text</label>
            <div className="flex gap-2">
              <Input
                placeholder="Enter your text here..."
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                className="flex-1"
              />
              <Button
                onClick={() => {
                  if (textInput.trim() && onAddText) {
                    onAddText(textInput.trim());
                    setTextInput("");
                    onClose?.();
                  }
                }}
                disabled={!textInput.trim()}
                size="sm"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          </div>
        </div>
      ) : (
        /* Symbols Grid */
        <div className="space-y-4">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3 max-h-48 sm:max-h-64 overflow-y-auto">
            <TooltipProvider>
              {filteredSymbols.map((symbol) => (
                <Tooltip key={symbol.id}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => {
                        onAddSymbol(symbol);
                        onClose?.();
                      }}
                      className="relative group focus:outline-none touch-manipulation"
                    >
                      <Card className="overflow-hidden transition-all hover:ring-2 hover:ring-primary/50 active:ring-2 active:ring-primary p-1 sm:p-2">
                        <div className="aspect-square relative flex items-center justify-center">
                          <img
                            src={symbol.image}
                            alt={symbol.name}
                            className="w-full h-full object-contain text-foreground"
                          />
                          <div className="absolute inset-0 bg-primary/10 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity flex items-center justify-center">
                            <Plus className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />
                          </div>
                        </div>
                      </Card>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <div className="space-y-1">
                      <p className="font-semibold">{symbol.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {symbol.meaning}
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              ))}
            </TooltipProvider>
          </div>

          {/* Alternative Text Option */}
          <div className="border-t pt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                Or add text instead:
              </label>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter text instead of symbol..."
                  value={alternativeTextInput}
                  onChange={(e) => setAlternativeTextInput(e.target.value)}
                  className="flex-1"
                />
                <Button
                  onClick={() => {
                    if (alternativeTextInput.trim() && onAddText) {
                      onAddText(alternativeTextInput.trim());
                      setAlternativeTextInput("");
                      onClose?.();
                    }
                  }}
                  disabled={!alternativeTextInput.trim()}
                  size="sm"
                  variant="outline"
                >
                  <Type className="h-4 w-4 mr-1" />
                  Add Text
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
        <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
        <p className="text-sm text-muted-foreground">
          Click on any symbol to add it to your sash. You can position and
          resize symbols on the canvas.
        </p>
      </div>
    </div>
  );
};
